import TrustSDK, {FrameMessageType} from '@yandex-trust-sdk/base'
import type {
  BindCardOptions,
  Currency,
  FrameMessageAbort,
  FrameMessageError,
  FrameMessageSuccess,
  Language,
  PaymentFrame,
} from '@yandex-trust-sdk/base'
import {MutableRefObject, useEffect, useRef, useState} from 'react'

type FrameMessageSuccessHandler = (event: FrameMessageSuccess) => void
type Props = {
  preserveQueryParams?: boolean
  currency?: string
  className?: string
  lang?: string
  device?: 'mobile' | 'desktop'
  onSuccess?: FrameMessageSuccessHandler
  onError?: (m: FrameMessageError) => void
  onAbort?: (m: FrameMessageAbort) => void
}

export type {FrameMessageAbort, FrameMessageError, FrameMessageSuccess, FrameMessageType, FrameMessageSuccessHandler}

export const useCardBoundRef = <T extends HTMLElement>(
  bindingUrl: string,
  props: Props,
): MutableRefObject<T | null> => {
  const [frame, setFrame] = useState<PaymentFrame | null>(null)
  const elementRef = useRef<T>(null)
  const propsRef = useRef(props)
  propsRef.current = props

  useEffect(() => {
    if (!bindingUrl) {
      return
    }

    let queryParams: Record<string, string> = {}

    if (!propsRef.current.preserveQueryParams) {
      const formAction = 'spin'
      const formTheme = 'default'
      const formOptions: Pick<BindCardOptions, 'mode' | 'modeSuccess' | 'theme' | 'lang' | 'currency'> = {
        mode: formAction,
        modeSuccess: formAction,
        theme: formTheme,
        lang: propsRef.current.lang! as Language,
        currency: propsRef.current.currency! as Currency,
      }

      queryParams = {
        ...formOptions,
        template_tag: `${propsRef.current.device || 'desktop'}/form`,
        developer_payload: JSON.stringify({
          'css-theme': formTheme,
          binding_completion_action: formAction,
          payment_completion_action: formAction,
          payment_success_action: formAction,
        }),
      }
    }

    TrustSDK.create()
      .bindCard({
        /**
         * @see https://a.yandex-team.ru/arcadia/trust/frontend/trust-web-sdk/packages/base/src/methods/bind-card.ts?rev=r11317347#L99
         */
        url: bindingUrl,
        queryParams,
      })
      .then(setFrame)
      // сейчас траст ничего вразумительного не отдает если падает ручка привязки
      // todo пофикисть когда обновится либа траста
      .catch(() => {
        if (propsRef.current.onError) {
          propsRef.current.onError({
            type: FrameMessageType.error,
            reason: 'too_many_cards',
          })
        }
      })
  }, [bindingUrl])

  useEffect(() => {
    const element = elementRef.current

    if (element && frame) {
      // подписываемся на события привязки
      propsRef.current.onSuccess && frame.on('success', propsRef.current.onSuccess)
      propsRef.current.onError && frame.on('error', propsRef.current.onError)
      propsRef.current.onAbort && frame.on('abort', propsRef.current.onAbort)

      // монтируем фрейм в DOM
      frame.mount(element, {className: propsRef.current.className})

      return () => {
        frame.unmount()
      }
    }
  }, [frame])

  return elementRef
}
